import * as Sentry from '@sentry/react';

import { showErrorMessage } from './errorHandling';
import { useCustomTranslation } from './translationHelper';

interface GeolocationConfig {
  enableHighAccuracy?: boolean;
  maximumAge?: number;
  timeout?: number;
  disableGeolocation?: boolean;
}

interface GeolocationPosition {
  coords: GeolocationCoordinates;
  timestamp: number;
}

interface GeolocationPositionError {
  code: number;
  message: string;
  PERMISSION_DENIED: number;
  POSITION_UNAVAILABLE: number;
  TIMEOUT: number;
}

export interface GeolocationResult {
  coords?: GeolocationCoordinates;
  error?: GeolocationPositionError;
}

function useGeolocation(config: GeolocationConfig) {
  const { enableHighAccuracy, maximumAge = 10000, timeout, disableGeolocation } = config;
  const { tError } = useCustomTranslation();

  const requestLocation = (required?: boolean): Promise<GeolocationResult> => {
    return new Promise((resolve) => {
      if (disableGeolocation) {
        resolve({});
        return;
      }
      let settled = false;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      const finish = (result: GeolocationResult) => {
        settled = true;
        clearTimeout(timeoutId);
        resolve(result);
      };

      const updateCoordinates = ({ coords }: GeolocationPosition) => {
        if (settled) return;
        finish({ coords });
      };

      const setError = (error: GeolocationPositionError) => {
        if (settled) return;
        finish({ error });
        const sentryError = new Error(
          `Geolocation error: ${error.message}, code: ${error.code}, permission denied code: ${error.PERMISSION_DENIED}, position unavailable code: ${error.POSITION_UNAVAILABLE}, timeout code: ${error.TIMEOUT}`
        );
        Sentry.captureException(sentryError, {
          tags: { ...error }
        });

        if (required) {
          showErrorMessage(tError('location_is_required'));
        }
      };

      if (!navigator.geolocation) {
        setError({
          code: 1,
          message: 'Geolocation unavailable',
          PERMISSION_DENIED: 1,
          POSITION_UNAVAILABLE: 2,
          TIMEOUT: 3
        });
        return;
      }

      // Bound the entire wait, including an unanswered permission prompt.
      if (timeout !== undefined && Number.isFinite(timeout)) {
        timeoutId = setTimeout(() => {
          setError({
            code: 3,
            message: 'Geolocation timed out',
            PERMISSION_DENIED: 1,
            POSITION_UNAVAILABLE: 2,
            TIMEOUT: 3
          });
        }, timeout);
      }

      navigator.geolocation.getCurrentPosition(updateCoordinates, setError, {
        enableHighAccuracy,
        maximumAge,
        timeout
      });
    });
  };

  return { requestLocation };
}

export { useGeolocation };
